// C.1 — Генетик Лиза (livlife_lisa).
//
// xlsx v6 dsl_trigger1:
//   event: turn_start, target: own_discard_random_unit, effect: resurrect
//   value: full_hp@random_empty_own_slot
//   condition: turn_number_even_and_own_discard_has_unit
//   → В начале своего чётного хода: воскресить СЛУЧАЙНОГО юнита из своего
//     сброса с full hp на случайную свободную ячейку.
//
// xlsx v6 dsl_active (Ф2, 1× за матч):
//   target: any_discard_choose_unit, effect: resurrect
//   value: full_hp@own_empty_slot
//   → UI: показать оба сброса (свой и вражеский) с фильтром только юниты.
//     Игрок выбирает любой юнит. Воскрешается с full hp на свою свободную
//     ячейку. Если выбран вражеский — он «играет за тебя» (угон без срока).
//     Сохраняются статы и keyword'ы из template.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { resurrectUnit } from '../resurrect.js';
import { onUnitSummoned } from '../../deus/hooks.js';

export const HERO_ID = 'livlife_lisa';

function findFreeSlot(player, rng) {
  if (!player?.field) return null;
  const free = [];
  for (let line = 0; line < 2; line++) {
    const row = player.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) free.push({ line, idx: s });
    }
  }
  if (free.length === 0) return null;
  const i = rng?.int ? rng.int(free.length) : Math.floor(Math.random() * free.length);
  return free[i];
}

function unitsInDiscard(player) {
  return (player?.discard || []).filter(c => c?.type === 'unit');
}

// === Trigger в начале своего хода (чётный) ===
export function lisaOnTurnStart(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return;
  if (p.hero.current?.silenced) return;
  const turn = state.currentTurn ?? 0;
  if (turn < 2 || turn % 2 !== 0) return;

  const candidates = unitsInDiscard(p);
  if (candidates.length === 0) return;
  const slot = findFreeSlot(p, state.rng);
  if (!slot) return;

  const rngIdx = state.rng?.int ? state.rng.int(candidates.length) : Math.floor(Math.random() * candidates.length);
  const deadCard = candidates[rngIdx];

  resurrectUnit(state, deadCard, playerIdx, slot, { hpMode: 'full_hp' });
  if (state.log) {
    state.log.add(`🧬 Лиза [${p.name}]: триггер — воскрешён ${deadCard.name_ru} из своего сброса`);
  }
}

// === Активка Ф2 ===

// Picker UI: вернуть всех юнитов из обоих сбросов.
//   Возвращает: [{ instanceId, name_ru, id, ownerIdx, isEnemy }, ...]
export function getLisaActiveDiscardChoices(state, playerIdx) {
  const result = [];
  for (let i = 0; i < (state.players?.length || 0); i++) {
    const sp = state.players[i];
    const units = unitsInDiscard(sp);
    for (const u of units) {
      result.push({
        instanceId: u.instanceId,
        name_ru: u.name_ru,
        id: u.id,
        ownerIdx: i,
        isEnemy: i !== playerIdx,
        unit: u
      });
    }
  }
  return result;
}

// Угон из чужого сброса: вручную сделать всё что делает resurrectUnit, но
// без проверки player.discard (она для текущего владельца сброса, а нам
// нужно положить на свою сторону).
function stealAndResurrect(state, stealerIdx, deadCard, fromOwnerIdx, slot) {
  const sourcePlayer = state.players[fromOwnerIdx];
  const stealer = state.players[stealerIdx];
  if (!sourcePlayer || !stealer) return false;

  const discardIdx = sourcePlayer.discard.indexOf(deadCard);
  if (discardIdx === -1) return false;

  // Изменения статов.
  const baseHp = deadCard.base?.hp ?? 0;
  if (!deadCard.current) deadCard.current = { atk: 0, armor: 0, hp: 0 };
  deadCard.current.hp = baseHp; // full_hp
  const maxArmor = (typeof deadCard.maxArmorWithMods === 'number')
    ? deadCard.maxArmorWithMods
    : (deadCard.base?.armor ?? 0);
  deadCard.current.armor = maxArmor;

  // Сброс состояния.
  deadCard.alive = true;
  deadCard.line = null;
  deadCard.slot = null;
  deadCard.lastLine = null;
  deadCard.lastSlot = null;
  deadCard.initiation = 0;
  deadCard.placedTurn = state.currentTurn;
  deadCard.revealed = true;
  deadCard.activated = false;
  deadCard.heroAttackedThisTurn = false;
  deadCard.counterAttackedThisPhase = false;

  // Смена владельца.
  deadCard.ownerId = stealerIdx;

  // Снять источники Молчания.
  if (deadCard.silenceSources) deadCard.silenceSources.clear();
  if (deadCard.current) deadCard.current.silenced = false;

  // Поставить на поле похитителя.
  try {
    stealer.placeCard(deadCard, slot.line, slot.idx);
  } catch {
    return false;
  }

  // Убрать из чужого сброса.
  sourcePlayer.discard.splice(discardIdx, 1);

  state.log?.add(
    `🧬 ${deadCard.name_ru} угнан Лизой из чужого сброса → играет за ${stealer.name}`
  );

  // Триггер onUnitSummoned для появления на нашей стороне — фракционные ауры (Передовая медицина, Подкрепление и т.п.).
  try { onUnitSummoned(state, stealerIdx, deadCard); } catch (e) { console.warn(e); }

  return true;
}

// Активка.
//   opts.targetInstanceId — instanceId юнита из getLisaActiveDiscardChoices.
//   opts.targetOwnerIdx — 0 или 1.
export function lisaActive(state, playerIdx, opts = {}) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.heroActiveUsedThisMatch) return { ok: false, reason: 'already_used' };

  const targetOwnerIdx = opts.targetOwnerIdx;
  const targetInstanceId = opts.targetInstanceId;
  if (targetOwnerIdx == null) return { ok: false, reason: 'no_owner' };
  if (targetInstanceId == null) return { ok: false, reason: 'no_target' };

  const sourcePlayer = state.players?.[targetOwnerIdx];
  if (!sourcePlayer?.discard) return { ok: false, reason: 'no_discard' };

  const deadCard = sourcePlayer.discard.find(c => c.instanceId === targetInstanceId);
  if (!deadCard) return { ok: false, reason: 'target_not_in_discard' };
  if (deadCard.type !== 'unit') return { ok: false, reason: 'not_unit' };

  const slot = findFreeSlot(p, state.rng);
  if (!slot) return { ok: false, reason: 'no_free_slot' };

  const isOwnDiscard = (targetOwnerIdx === playerIdx);
  let ok;
  if (isOwnDiscard) {
    ok = resurrectUnit(state, deadCard, playerIdx, slot, { hpMode: 'full_hp' });
  } else {
    ok = stealAndResurrect(state, playerIdx, deadCard, targetOwnerIdx, slot);
  }
  if (!ok) return { ok: false, reason: 'resurrect_failed' };

  p.heroActiveUsedThisMatch = true;
  const tag = isOwnDiscard ? 'из своего сброса' : 'из вражеского сброса (угон)';
  if (state.log) {
    state.log.add(`🧬 Лиза [${p.name}]: активка — ${deadCard.name_ru} воскрешён ${tag} (full hp)`);
  }
  return { ok: true };
}

export function lisaActiveAvailable(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return false;
  if (p.heroActiveUsedThisMatch) return false;
  if (p.hero.current?.silenced) return false;
  // Должен быть хотя бы один юнит в любом из сбросов И свободная ячейка.
  let hasUnitInDiscard = false;
  for (const sp of state.players) {
    if (unitsInDiscard(sp).length > 0) { hasUnitInDiscard = true; break; }
  }
  if (!hasUnitInDiscard) return false;
  return !!findFreeSlot(p, null);
}

registerImplementedAbility(HERO_ID, 'trigger', 'active');
